import { useState, useEffect, useCallback } from 'react';
import reviewApiService from '../services/reviewApiService';

/**
 * Main review state management hook
 * Handles session persistence, navigation, and auto-save functionality
 */
export const useReviewState = (initialCreatorIds = []) => {
  const [sessionId, setSessionId] = useState(null);
  const [creators, setCreators] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [reviews, setReviews] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const [totalCreators, setTotalCreators] = useState(0);
  const [hasMoreCreators, setHasMoreCreators] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // localStorage key for session persistence
  const STORAGE_KEY = 'reviewSession';

  /**
   * Load session from localStorage
   */
  const loadSessionFromStorage = useCallback(() => {
    try {
      const savedSession = localStorage.getItem(STORAGE_KEY);
      if (savedSession) {
        const parsedSession = JSON.parse(savedSession);
        setSessionId(parsedSession.sessionId);
        setCreators(parsedSession.creators || []);
        setCurrentIndex(parsedSession.currentIndex || 0);
        setReviews(parsedSession.reviews || {});
        return parsedSession;
      }
    } catch (error) {
      console.error('Error loading session from storage:', error);
    }
    return null;
  }, []);

  /**
   * Save session to localStorage
   */
  const saveSessionToStorage = useCallback((sessionData) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionData));
    } catch (error) {
      console.error('Error saving session to storage:', error);
    }
  }, []);

  /**
   * Clear session from localStorage
   */
  const clearSessionFromStorage = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  /**
   * Start a new review session - loads all creators at once
   */
  const startSession = useCallback(async (creatorIds, options = {}) => {
    try {
      setLoading(true);
      setError(null);

      // No more lazy loading - load all creators at once
      const sessionData = await reviewApiService.startReviewSession(creatorIds, {});

      console.log('Raw session data received:', sessionData);

      // Handle backend response structure
      const sessionId = sessionData.sessionId || sessionData._id || sessionData.id;
      const sessionCreators = sessionData.creatorIds || sessionData.creators || [];

      setSessionId(sessionId);
      setCreators(sessionCreators);
      setCurrentIndex(0);
      setReviews({});
      setTotalCreators(creatorIds.length);
      setHasMoreCreators(false); // All loaded at once

      console.log('Session started:', {
        sessionId,
        creatorsLoaded: sessionCreators.length,
        totalCreators: sessionData.meta?.totalCreators || creatorIds.length,
        hasMore: sessionData.meta?.hasMore || false
      });

      // No need to load reviews anymore - all data is in Creator model!
      console.log(`📊 Session started with ${sessionCreators.length} creators`);
      console.log(`✅ All review data will be loaded from Creator model directly`);

      // Initialize empty reviews object since all data is in Creator model
      setReviews({});

      // Save to localStorage
      const sessionToSave = {
        sessionId: sessionId,
        creators: sessionCreators,
        currentIndex: 0,
        reviews: {}  // Empty reviews since data is in Creator model
      };
      saveSessionToStorage(sessionToSave);

      return sessionData;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [saveSessionToStorage]);

  /**
   * Load existing session
   */
  const loadSession = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);

      const sessionData = await reviewApiService.getSession(id);

      // Handle backend response structure
      const sessionId = sessionData.sessionId || sessionData._id || sessionData.id;
      const sessionCreators = sessionData.creatorIds || sessionData.creators || [];

      setSessionId(sessionId);
      setCreators(sessionCreators);
      setCurrentIndex(sessionData.currentIndex || 0);
      setReviews(sessionData.reviews || {});

      return sessionData;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update current creator data locally (before saving to backend)
   */
  const updateCurrentReview = useCallback((reviewData) => {
    const currentCreator = creators[currentIndex];
    if (!currentCreator) return;

    // Update the creator object with the new review data
    const updatedCreator = { ...currentCreator };

    // Update review scores
    if (reviewData.contentQuality !== undefined || reviewData.followsInstructions !== undefined || reviewData.deliversOnTime !== undefined) {
      updatedCreator.reviewScores = {
        ...updatedCreator.reviewScores,
        contentQuality: reviewData.contentQuality ?? updatedCreator.reviewScores?.contentQuality ?? 5,
        followsInstructions: reviewData.followsInstructions ?? updatedCreator.reviewScores?.followsInstructions ?? 5,
        deliversOnTime: reviewData.deliversOnTime ?? updatedCreator.reviewScores?.deliversOnTime ?? 5,
        takeoverScore: (reviewData.contentQuality ?? updatedCreator.reviewScores?.contentQuality ?? 5) +
                      (reviewData.followsInstructions ?? updatedCreator.reviewScores?.followsInstructions ?? 5) +
                      (reviewData.deliversOnTime ?? updatedCreator.reviewScores?.deliversOnTime ?? 5)
      };
    }

    // Update fast turnaround
    if (reviewData.fastTurnaround !== undefined) {
      updatedCreator.fastTurnaround = reviewData.fastTurnaround;
    }

    // Update notes
    if (reviewData.internalNotes !== undefined || reviewData.feedbackToCreator !== undefined) {
      updatedCreator.reviewNotes = {
        ...updatedCreator.reviewNotes,
        internalNotes: reviewData.internalNotes ?? updatedCreator.reviewNotes?.internalNotes ?? '',
        feedbackToCreator: reviewData.feedbackToCreator ?? updatedCreator.reviewNotes?.feedbackToCreator ?? ''
      };
    }

    // Update status
    if (reviewData.creatorStatus !== undefined) {
      updatedCreator.status = reviewData.creatorStatus;
    }

    // Update pricing
    if (reviewData.defaultPayment !== undefined || reviewData.qrtPayment !== undefined || reviewData.shortformPayment !== undefined) {
      updatedCreator.wallet = {
        ...updatedCreator.wallet,
        defaultPayment: reviewData.defaultPayment ?? updatedCreator.wallet?.defaultPayment ?? 0,
        qrtPayment: reviewData.qrtPayment ?? updatedCreator.wallet?.qrtPayment ?? 0,
        shortFormPayment: reviewData.shortformPayment ?? updatedCreator.wallet?.shortFormPayment ?? 0
      };
    }

    // Update creators array with the updated creator
    const updatedCreators = [...creators];
    updatedCreators[currentIndex] = updatedCreator;
    setCreators(updatedCreators);

    // Also keep track in reviews for backward compatibility
    const updatedReviews = {
      ...reviews,
      [currentCreator._id]: {
        ...reviews[currentCreator._id],
        ...reviewData,
        creatorId: currentCreator._id,
        lastUpdated: new Date().toISOString()
      }
    };
    setReviews(updatedReviews);

    // Update localStorage
    const sessionToSave = {
      sessionId,
      creators: updatedCreators,
      currentIndex,
      reviews: updatedReviews
    };
    saveSessionToStorage(sessionToSave);
  }, [creators, currentIndex, reviews, sessionId, saveSessionToStorage]);

  /**
   * Save current review to backend
   */
  const saveCurrentReview = useCallback(async () => {
    const currentCreator = creators[currentIndex];
    if (!currentCreator) {
      console.log('No current creator to save review for');
      return false;
    }

    // Even if there's no review data yet, we should save what we have
    const reviewData = reviews[currentCreator._id] || {};

    // Ensure we have the creator ID
    const dataToSave = {
      ...reviewData,
      creatorId: currentCreator._id
    };

    console.log('Saving review data:', {
      dataToSave,
      hasContentQuality: 'contentQuality' in dataToSave,
      hasFollowsInstructions: 'followsInstructions' in dataToSave,
      hasDeliversOnTime: 'deliversOnTime' in dataToSave,
      hasFastTurnaround: 'fastTurnaround' in dataToSave,
      fullData: JSON.stringify(dataToSave, null, 2)
    });

    try {
      setSaving(true);
      const savedReview = await reviewApiService.saveReview(dataToSave);

      // Update the local data with the saved review
      if (savedReview) {
        console.log('✅ Review saved successfully to MongoDB');

        // The review data is now saved in the Creator model, so we don't need to update reviews
        // The updateCurrentReview function has already updated the local creator object

        setReviews(prev => ({
          ...prev,
          [currentCreator._id]: savedReview
        }));

        // Update localStorage
        const sessionToSave = {
          sessionId,
          creators,
          currentIndex,
          reviews: {
            ...reviews,
            [currentCreator._id]: savedReview
          }
        };
        saveSessionToStorage(sessionToSave);
      }

      setLastSaveTime(new Date());
      return true;
    } catch (error) {
      console.error('Error saving review:', error);
      setError(error.message);
      return false;
    } finally {
      setSaving(false);
    }
  }, [creators, currentIndex, reviews, sessionId, saveSessionToStorage]);

  /**
   * Navigate to next creator
   */
  const goToNext = useCallback(() => {
    if (currentIndex < creators.length - 1) {
      // Move to next creator
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);

      const nextCreator = creators[newIndex];
      console.log(`➡️ Navigating to next creator: ${nextCreator?.name}`);

      // Update localStorage
      const sessionToSave = {
        sessionId,
        creators,
        currentIndex: newIndex,
        reviews
      };
      saveSessionToStorage(sessionToSave);
    }
  }, [currentIndex, creators.length, sessionId, creators, reviews, saveSessionToStorage]);

  /**
   * Navigate to previous creator
   */
  const goToPrevious = useCallback(() => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);

      const prevCreator = creators[newIndex];
      console.log(`⬅️ Navigating to previous creator: ${prevCreator?.name}`);

      // Update localStorage
      const sessionToSave = {
        sessionId,
        creators,
        currentIndex: newIndex,
        reviews
      };
      saveSessionToStorage(sessionToSave);
    }
  }, [currentIndex, sessionId, creators, reviews, saveSessionToStorage]);

  /**
   * Skip current creator
   */
  const skipCurrent = useCallback(async () => {
    // Save current review before skipping
    await saveCurrentReview();
    goToNext();
  }, [goToNext, saveCurrentReview]);

  /**
   * End review session
   */
  const endSession = useCallback(() => {
    setSessionId(null);
    setCreators([]);
    setCurrentIndex(0);
    setReviews({});
    clearSessionFromStorage();
  }, [clearSessionFromStorage]);

  // Auto-save disabled to prevent screen jumping
  // Users can manually save or save on navigation
  // useEffect(() => {
  //   if (!sessionId) return;

  //   const interval = setInterval(() => {
  //     saveCurrentReview();
  //   }, 30000); // 30 seconds

  //   return () => clearInterval(interval);
  // }, [sessionId, saveCurrentReview]);

  // Load session on mount if available (disabled to prevent conflicts)
  // useEffect(() => {
  //   const savedSession = loadSessionFromStorage();
  //   if (savedSession && !sessionId && initialCreatorIds.length === 0) {
  //     loadSession(savedSession.sessionId).catch(console.error);
  //   }
  // }, [loadSessionFromStorage, loadSession, sessionId, initialCreatorIds.length]);

  // Note: Review loading is handled in:
  // 1. startSession - loads all reviews for all creators upfront
  // 2. goToNext/goToPrevious - loads fresh review data when navigating
  // This prevents duplicate API calls and ensures data is always fresh

  // Get current creator
  const currentCreator = creators[currentIndex] || null;
  const currentReview = currentCreator ? reviews[currentCreator._id] : null;

  // Progress calculation - all creators loaded
  const progress = creators.length > 0 ? {
    current: currentIndex + 1,
    total: creators.length,
    percentage: Math.round(((currentIndex + 1) / creators.length) * 100)
  } : { current: 0, total: 0, percentage: 0 };

  /**
   * Load more creators (lazy loading)
   */
  const loadMoreCreators = useCallback(async (batchSize = 10) => {
    console.log('loadMoreCreators called:', {
      sessionId,
      hasMoreCreators,
      loadingMore,
      creatorsLength: creators.length
    });

    if (!sessionId || !hasMoreCreators || loadingMore) {
      console.log('loadMoreCreators early return');
      return;
    }

    try {
      setLoadingMore(true);
      setError(null);

      // Calculate the correct offset based on how many creators are already loaded
      const offset = creators.length;
      console.log('Loading more creators with offset:', offset);

      const response = await reviewApiService.loadMoreCreators(sessionId, {
        batchSize,
        offset
      });
      console.log('loadMoreCreators response:', response);
      const newCreators = response.creators || [];

      if (newCreators.length > 0) {
        setCreators(prev => [...prev, ...newCreators]);
        setHasMoreCreators(response.hasMore || false);

        // Update localStorage
        const sessionToSave = {
          sessionId,
          creators: [...creators, ...newCreators],
          currentIndex,
          reviews,
          totalCreators,
          hasMore: response.hasMore || false
        };
        saveSessionToStorage(sessionToSave);
      }

      return response;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoadingMore(false);
    }
  }, [sessionId, hasMoreCreators, loadingMore, creators, currentIndex, reviews, totalCreators, saveSessionToStorage]);

  // No auto-loading needed - all creators loaded at session start

  return {
    // State
    sessionId,
    creators,
    currentIndex,
    currentCreator,
    currentReview,
    reviews,
    loading,
    saving,
    error,
    progress,
    lastSaveTime,
    totalCreators,
    hasMoreCreators,
    loadingMore,

    // Actions
    startSession,
    loadSession,
    endSession,
    updateCurrentReview,
    saveCurrentReview,
    goToNext,
    goToPrevious,
    skipCurrent,
    loadMoreCreators,

    // Helpers
    hasNext: currentIndex < creators.length - 1,
    hasPrevious: currentIndex > 0,
    isSessionActive: !!sessionId
  };
};